import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import {
    Pencil,
    X,
    MapPin,
    User,
    Clock,
    CalendarCheck,
    CircleX,
    CircleSlash,
    Download,
} from 'lucide-react';

import type { IShowing } from 'src/api/landlord-showings-api';
import type { ELeadStage } from 'src/services/api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useProperties } from 'src/services/api';
import { useUpdateLeadStage } from 'src/services/api/leads/use-update-lead-stage';
import { useConfirmShowing } from 'src/services/api/showings/use-confirm-showing';
import { useCancelShowing } from 'src/services/api/showings/use-cancel-showing';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';
import SelectLeadStage from 'src/components/input/select-lead-stage/SelectLeadStage';
import { CANCELED_TOUR_COLOR } from 'src/pages/landlord/tours/components/calendar/property-colors';
import { buildPropertyCalendarMaps } from 'src/pages/landlord/tours/components/calendar/tour-calendar-utils';
import {
    createTourIcsUrl,
    downloadUrl,
} from 'src/pages/landlord/tours/components/calendar/tour-ics';
import { formatDateTime, getUserTimezone } from 'src/utils/format-date-time';
import { CALENDAR_ACCENT } from 'src/pages/landlord/tours/components/calendar/calendar-theme';
import styles from 'src/pages/landlord/tours/components/calendar/ToursCalendar.module.css';

// Icon color for disabled (non-owner) actions, so they read as greyed-out.
const DISABLED_ICON_COLOR = '#bcc2c9';

interface Props {
    tour: IShowing | null;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onEdit: (tour: IShowing) => void;
}

/**
 * Read-only detail card anchored to a clicked tour (calendar event or lead
 * sidebar card), modeled on the Google Calendar event popup. The lead's stage
 * is editable inline here; the pencil opens the full create/update tour modal
 * for everything else.
 */
export default function TourDetailPopover({
    tour,
    anchorEl,
    onClose,
    onEdit,
}: Props) {
    const { leadsByUuid, userSettings, loggedInUser } = useAuthenticatedState();
    const { data: teamMembers = [] } = useTeamMembers({ includeInactive: true });
    const { data: properties } = useProperties();
    const navigate = useNavigate();
    const location = useLocation();
    const timezone = getUserTimezone(userSettings);

    const lead = tour ? leadsByUuid.get(tour.lead_uuid) : undefined;
    const { mutate: updateLeadStage } = useUpdateLeadStage(lead?.uuid ?? '');
    const { mutateAsync: confirmShowing } = useConfirmShowing();
    const { mutateAsync: cancelShowing } = useCancelShowing();

    // Track confirm/cancel locally so the popover reflects the change
    // immediately, rather than reading the stale tour object the parent
    // captured when the popover opened.
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isCanceled, setIsCanceled] = useState(false);

    useEffect(() => {
        if (tour) {
            setIsConfirmed(tour.is_confirmed);
            setIsCanceled(tour.is_canceled);
        }
    }, [tour]);

    // Root-parent color, matching the event bar and the sidebar legend. Must
    // stay above the early return: hooks may not render conditionally.
    const { propertyColorById } = useMemo(
        () => buildPropertyCalendarMaps(properties ?? []),
        [properties]
    );

    if (!tour) {
        return null;
    }

    const propertyList = properties ?? [];
    const property = propertyList.find((p) => p.id === tour.property_id);


    // Leads missing from the (archived-excluding) leads list still have their
    // name embedded on the tour by the backend join, matching the calendar grid.
    const leadName = lead?.name || lead?.email || tour.lead?.name || 'Archived lead';
    const ownerName =
        teamMembers.find((m) => m.id === lead?.owner)?.name ?? 'Unassigned';

    // Only the lead owner may confirm/edit/cancel the tour, matching the
    // create/edit modal's ownership rule.
    const isOwner = lead?.owner === loggedInUser?.id;

    const goToLeadMessages = () => {
        onClose();
        const searchParams = new URLSearchParams();
        searchParams.set('lead_uuid', tour.lead_uuid);
        searchParams.set(
            'back_path',
            `${location.pathname}${location.search}`
        );
        navigate(`/landlord/inner/messages/from-lead?${searchParams.toString()}`);
    };

    // No explicit refetch here: the confirm/cancel mutations invalidate the
    // tours query prefix themselves, which refreshes every consumer (tours
    // page and lead sidebar alike).
    const handleConfirm = async () => {
        const next = !isConfirmed;
        await confirmShowing({ tourId: tour.id, value: next });
        setIsConfirmed(next);
    };

    const handleCancel = async () => {
        const next = !isCanceled;
        await cancelShowing({ tourId: tour.id, value: next });
        setIsCanceled(next);
    };

    const handleDownload = async () => {
        const url = await createTourIcsUrl(
            tour,
            property?.name || 'Property not set',
            leadName
        );
        downloadUrl(url, 'invite.ics');
    };

    const dateLine = `${formatDateTime(tour.start_time, 'dddd, MMMM D', '', timezone)}  ·  ${formatDateTime(tour.start_time, 'h:mm A', '', timezone)}`;
    const accent = propertyColorById.get(tour.property_id) ?? CANCELED_TOUR_COLOR;

    const handleStageChange = (newStage: ELeadStage) => {
        if (lead) {
            updateLeadStage({ stage: newStage });
        }
    };

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
            transformOrigin={{ vertical: 'center', horizontal: 'left' }}
            slotProps={{ paper: { className: styles.tourPopover } }}
        >
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                className={styles.popoverActions}
            >
                <Tooltip
                    title={
                        isOwner ?
                            (isConfirmed ? 'Unconfirm' : 'Confirm') :
                            'Only the lead owner can confirm'
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleConfirm}
                            disabled={!isOwner}
                            className={isConfirmed && isOwner ? styles.confirmedButton : undefined}
                        >
                            <CalendarCheck
                                size={18}
                                color={
                                    !isOwner ?
                                        DISABLED_ICON_COLOR :
                                        (isConfirmed ? '#fff' : CALENDAR_ACCENT)
                                }
                            />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={isOwner ? 'Edit' : 'Only the lead owner can edit'}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => onEdit(tour)}
                            disabled={!isOwner}
                        >
                            <Pencil size={18} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip
                    title={
                        isOwner ?
                            (isCanceled ? 'Uncancel' : 'Cancel') :
                            'Only the lead owner can cancel'
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleCancel}
                            disabled={!isOwner}
                        >
                            {isCanceled ? (
                                <CircleSlash
                                    size={18}
                                    color={isOwner ? CALENDAR_ACCENT : DISABLED_ICON_COLOR}
                                />
                            ) : (
                                <CircleX
                                    size={18}
                                    color={isOwner ? '#ea4335' : DISABLED_ICON_COLOR}
                                />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Download invite">
                    <IconButton size="small" onClick={handleDownload}>
                        <Download size={18} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                    <IconButton size="small" onClick={onClose}>
                        <X size={18} />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Box className={styles.popoverBody}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                        className={styles.popoverAccent}
                        style={{ backgroundColor: accent }}
                    />
                    <Box className={styles.popoverHeaderText}>
                        <Tooltip title={`Tour with ${leadName}`}>
                            <Typography
                                variant="h6"
                                onClick={goToLeadMessages}
                                className={`${styles.popoverTitle} ${styles.popoverTitleLink} ${isCanceled ? styles.popoverTitleCanceled : ''}`}
                            >
                                {`Tour with ${leadName}`}
                            </Typography>
                        </Tooltip>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            className={styles.detailText}
                        >
                            {dateLine}
                        </Typography>
                    </Box>
                </Stack>

                <Stack spacing={1.5} className={styles.popoverDetails}>
                    <DetailRow icon={<MapPin size={18} />}>
                        <Tooltip title={property?.name ?? 'Property'}>
                            <Typography variant="body2" className={styles.detailText}>
                                {property?.name ?? 'Property'}
                            </Typography>
                        </Tooltip>
                    </DetailRow>

                    <DetailRow icon={<User size={18} />}>
                        <Tooltip title={ownerName}>
                            <Typography variant="body2" className={styles.detailText}>
                                {ownerName}
                            </Typography>
                        </Tooltip>
                    </DetailRow>

                    {isConfirmed && (
                        <DetailRow icon={<Clock size={18} />} align="center">
                            <Box
                                className={styles.confirmedBadge}
                            >
                                Confirmed
                            </Box>
                        </DetailRow>
                    )}

                    {lead?.stage && (
                        <DetailRow icon={null} align="center">
                            <SelectLeadStage
                                value={lead.stage}
                                onChange={handleStageChange}
                            />
                        </DetailRow>
                    )}
                </Stack>
            </Box>
        </Popover>
    );
}

function DetailRow({
    icon,
    children,
    align = 'flex-start',
}: {
    icon: React.ReactNode;
    children: React.ReactNode;
    align?: 'flex-start' | 'center';
}) {
    return (
        <Stack direction="row" spacing={1.5} alignItems={align}>
            <Box className={styles.detailIcon}>
                {icon}
            </Box>
            {children}
        </Stack>
    );
}
